import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { num } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Network, ChevronRight, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { EstimateBadge } from "@/components/EstimateHint";

const Bar = ({ label, desc, value, max, color, total, testid, onClick }) => {
  const pct = max > 0 ? Math.max(value > 0 ? 6 : 0, (value / max) * 100) : 0;
  const share = total > 0 ? Math.round((value / total) * 100) : null;
  return (
    <button type="button" onClick={onClick} className="flex w-full items-center gap-3 rounded-md px-1 py-1 text-left transition-colors hover:bg-muted/50" data-testid={testid}>
      <div className="w-44 shrink-0">
        <div className="flex items-center gap-1.5 text-xs font-semibold text-foreground">
          <span className="inline-block h-2.5 w-2.5 shrink-0 rounded-sm" style={{ backgroundColor: color }} />
          <span className="truncate">{label}</span>
        </div>
        {desc && <div className="ml-4 mt-0.5 text-[10px] leading-tight text-muted-foreground">{desc}</div>}
      </div>
      <div className="h-4 flex-1 overflow-hidden rounded-full bg-muted">
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
      <div className="flex w-20 shrink-0 items-center justify-end gap-1 text-right font-mono text-sm font-bold">
        <span>{num(value)}{share != null && <span className="ml-1 text-[10px] font-normal text-muted-foreground">{share}%</span>}</span>
        <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
      </div>
    </button>
  );
};

const Metric = ({ label, value, tone = "" }) => (
  <div className={`rounded-md border border-border p-3 ${tone}`}>
    <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
    <div className="mt-1 font-heading text-xl font-black">{value}</div>
  </div>
);

// Filtri coerenti con il conteggio del backend (/dashboard/profilazione-rete)
const CAT_FILTER = (k) => (p) => (p.profilazione || {}).categoria_auto === k;
const STATO_FILTER = {
  in_crescita: (p) => (p.profilazione || {}).trend === "crescita",
  a_strategici: (p) => { const pr = p.profilazione || {}; return pr.categoria_auto === "A" && pr.stato_commerciale === "strategico"; },
  a_opportunita: (p) => { const pr = p.profilazione || {}; return pr.categoria_auto === "A" && pr.stato_commerciale === "opportunita"; },
  da_riattivare: (p) => (p.profilazione || {}).stato_commerciale === "da_riattivare",
  a_rischio: (p) => (p.profilazione || {}).stato_commerciale === "a_rischio",
};

export default function ProfilazioneRete() {
  const [d, setD] = useState(null);
  const [partners, setPartners] = useState([]);
  const [sel, setSel] = useState(null); // { title, desc, color, items }
  const [dl, setDl] = useState(false);
  const navigate = useNavigate();
  useEffect(() => {
    api.get("/dashboard/profilazione-rete").then((r) => setD(r.data)).catch(() => {});
    api.get("/partners").then((r) => setPartners(r.data || [])).catch(() => {});
  }, []);
  if (!d) return null;
  const c = d.counts;

  const openList = (title, desc, color, filterFn) => {
    const items = partners.filter(filterFn).sort((a, b) => ((b.profilazione || {}).potential_score || 0) - ((a.profilazione || {}).potential_score || 0));
    setSel({ title, desc, color, items });
  };

  const totCat = (c.A || 0) + (c.B || 0) + (c.C || 0);
  const cat = [
    { label: "Categoria A", desc: "Alto potenziale — partner strategici da presidiare", value: c.A || 0, color: "#FDD003", key: "A" },
    { label: "Categoria B", desc: "Potenziale medio — da far crescere", value: c.B || 0, color: "#F59E0B", key: "B" },
    { label: "Categoria C", desc: "Potenziale contenuto — presidio leggero / massa", value: c.C || 0, color: "#94A3B8", key: "C" },
  ];
  const stato = [
    { label: "In crescita", desc: "Acquisti in aumento nel periodo", value: c.in_crescita || 0, color: "#22C55E", key: "in_crescita" },
    { label: "A · Strategici", desc: "Categoria A ad alta penetrazione e attività", value: c.a_strategici || 0, color: "#FDD003", key: "a_strategici" },
    { label: "A · Opportunità", desc: "Categoria A con penetrazione ancora bassa", value: c.a_opportunita || 0, color: "#F59E0B", key: "a_opportunita" },
    { label: "Da riattivare", desc: "Storico presente ma fermo da tempo", value: c.da_riattivare || 0, color: "#FB923C", key: "da_riattivare" },
    { label: "A rischio", desc: "Alto valore in calo o inattivo — intervenire", value: c.a_rischio || 0, color: "#EF4444", key: "a_rischio" },
  ];
  const maxStato = Math.max(1, ...stato.map((s) => s.value));
  const scaricaRegole = async () => {
    setDl(true);
    try {
      const r = await api.get("/reports/regole-azioni/pdf", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = "regole-azioni-torri.pdf"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Errore nel download del PDF"); } finally { setDl(false); }
  };

  return (
    <Card className="p-6" data-testid="profilazione-rete-card">
      <div className="mb-1 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2"><Network className="h-4 w-4 text-primary" /><h2 className="font-heading text-lg font-bold tracking-tight">Profili Clienti</h2></div>
        <Button variant="outline" size="sm" onClick={scaricaRegole} disabled={dl} data-testid="download-regole-btn">
          {dl ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />} Regole azioni (PDF)
        </Button>
      </div>
      <p className="mb-5 text-[11px] text-muted-foreground">Clicca una categoria o uno stato per vedere l'elenco dei clienti.</p>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div>
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Categorie clienti</h3>
            {c.incomplete > 0 && <span className="rounded-full border border-amber-500/30 bg-amber-500/15 px-2 py-0.5 text-[11px] font-semibold text-amber-400" data-testid="incomplete-badge">{num(c.incomplete)} profili incompleti</span>}
          </div>
          <p className="mb-3 text-[11px] leading-tight text-muted-foreground">Classificano il <b>potenziale di business</b> del cliente con TORRI: <b>A</b> alto, <b>B</b> medio, <b>C</b> contenuto. Indicano <i>quanto vale</i> il cliente in prospettiva, a prescindere da quanto acquista oggi.</p>
          <div className="space-y-3">
            {cat.map((x) => <Bar key={x.label} {...x} max={totCat} total={totCat} testid={`cat-bar-${x.label}`} onClick={() => openList(x.label, x.desc, x.color, CAT_FILTER(x.key))} />)}
          </div>
          {c.stimati > 0 && (
            <div className="mt-3 flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 p-2" data-testid="cat-stima-note">
              <EstimateBadge className="mt-0.5 shrink-0" />
              <span className="text-[11px] leading-tight text-muted-foreground">Le categorie di {num(c.stimati)} clienti sono stimate dai dati di vendita (dati strutturali mancanti). Inserire i dati reali migliora la precisione.</span>
            </div>
          )}
        </div>

        <div>
          <h3 className="mb-1 text-xs font-bold uppercase tracking-wide text-muted-foreground">Stato commerciale</h3>
          <p className="mb-3 text-[11px] leading-tight text-muted-foreground">Fotografia <b>operativa</b> del rapporto <i>oggi</i>: quanto il cliente è attivo, in crescita, un'opportunità da sviluppare, da riattivare o a rischio — in base ad acquisti, penetrazione TORRI e ultimo ordine.</p>
          <div className="space-y-3">
            {stato.map((x) => <Bar key={x.label} {...x} max={maxStato} testid={`stato-bar-${x.label}`} onClick={() => openList(x.label, x.desc, x.color, STATO_FILTER[x.key])} />)}
          </div>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-4">
        {d.potenziale_torri_totale > 0 ? (
          <Metric label="Potenziale TORRI" value={`${num(d.potenziale_torri_totale)} kWp`} />
        ) : (
          <Metric label="Potenziale TORRI (stima)" value={d.potenziale_torri_stimato_totale ? `~${num(d.potenziale_torri_stimato_totale)} kWp` : "—"} tone="border-amber-500/30 bg-amber-500/10" />
        )}
        <Metric label="kWp TORRI LTM" value={`${num(d.kwp_torri_ltm)} kWp`} tone="border-primary/30 bg-primary/10" />
        <Metric label="Penetrazione media" value={d.penetrazione_media != null ? `${d.penetrazione_media}%` : "—"} />
        <Metric label="Non espresso" value={`${num(d.potenziale_non_espresso)} kWp`} tone="bg-muted/40" />
      </div>

      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="max-h-[80vh] overflow-hidden" data-testid="profilazione-list-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="inline-block h-3 w-3 rounded-sm" style={{ backgroundColor: sel?.color }} />
              {sel?.title} <span className="text-sm font-normal text-muted-foreground">· {sel?.items?.length || 0} clienti</span>
            </DialogTitle>
            <DialogDescription>{sel?.desc}</DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] space-y-1 overflow-y-auto pr-1">
            {(sel?.items || []).map((p) => {
              const pr = p.profilazione || {};
              return (
                <button
                  key={p.id}
                  onClick={() => { navigate(`/partners/${p.id}`); setSel(null); }}
                  className="flex w-full items-center justify-between gap-3 rounded-md border border-border px-3 py-2 text-left transition-colors hover:border-primary/50 hover:bg-muted/50"
                  data-testid={`prof-list-item-${p.id}`}
                >
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold">{p.name}</div>
                    <div className="truncate text-[11px] text-muted-foreground">
                      {[p.city, p.province].filter(Boolean).join(", ") || "—"}
                      {p.agency_name ? ` · ${p.agency_name}` : ""}
                      {pr.potential_stimato ? " · stima" : ""}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-2 text-right">
                    <span className="font-mono text-xs text-muted-foreground">{num((p.ltm || {}).kwp_torri || 0)} kWp</span>
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                </button>
              );
            })}
            {sel && sel.items.length === 0 && <div className="py-8 text-center text-sm text-muted-foreground">Nessun cliente in questa categoria.</div>}
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
